import * as React from 'react'
import { useLocation, useParams } from 'react-router-dom'
import VehicleViewControlBarLayout from './VehicleViewControlBarLayout'

import { useVehicles } from '@/modules/shared/context/vehicles/VehiclesContext'
import useChangeVehicleParams from '../../hooks/useChangeVehicleParams'
import { useDate } from '@/modules/drivers/stores/dateStore'
import DatePickerSingleButton from '@/modules/drivers/shared/datepickers/DatePickerSingleButton'
import MonthPickerButton from '@/modules/drivers/shared/datepickers/MonthPickerButton'
import YearPickerButton from '@/modules/drivers/shared/datepickers/YearPickerButton'
import { useActivity } from '@/modules/vehicles/stores/activityStore'
import { VehicleModel } from '@/app/models'
import useVehicleService from '../../services/VehicleService'

const VehicleActivityBarWithDate: React.FC = () => {
  const { companyId, vehicleId, date, year: yearParam } = useParams<{
    companyId: string
    vehicleId: string
    date?: string
    year?: string
  }>()
  const { pathname } = useLocation()
  const { vehiclesIds } = useVehicles()
  const { single: singleDate, month, year, setSingle, setYear } = useDate()
  const { getVehicle } = useVehicleService()
  const { goNextVehicleInActivity, goPrevVehicleInActivity, replaceUrlParam } =
    useChangeVehicleParams()
  const { type, setType } = useActivity()
  const [vehicle, setVehicle] = React.useState<VehicleModel | null>(null)

  // Guards against feedback loops where URL→store→URL would churn.
  const isSyncingFromUrl = React.useRef(false)

  // Derive activity type from the URL path.
  React.useEffect(() => {
    if (pathname.includes('/vehicleDailyActivity/')) setType('daily')
    else if (pathname.includes('/vehicleWeeklyActivity/')) setType('weekly')
    else if (pathname.includes('/vehicleCalendar/')) setType('yearly')
  }, [pathname, setType])

  // Hydrate store's `single` from URL on daily/weekly routes.
  React.useEffect(() => {
    if ((type === 'daily' || type === 'weekly') && date && singleDate !== date) {
      isSyncingFromUrl.current = true
      setSingle(date)
      setTimeout(() => {
        isSyncingFromUrl.current = false
      }, 0)
    }
  }, [date, type, setSingle])

  // Hydrate `year` store slot from URL on yearly routes.
  React.useEffect(() => {
    if (type === 'yearly' && yearParam) setYear(yearParam)
  }, [yearParam, type, setYear])

  React.useEffect(() => {
    if (!vehicleId || !companyId) return
    let cancelled = false
    getVehicle({ companyId, vehicleId })
      .then((res) => {
        if (!cancelled) setVehicle(res)
      })
      .catch((err) => {
        if (!cancelled) {
          setVehicle(null)
          console.error(err)
        }
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, companyId])

  // When the store's `single` changes (e.g. user picks a date), sync URL.
  React.useEffect(() => {
    if (
      !isSyncingFromUrl.current &&
      singleDate &&
      (type === 'daily' || type === 'weekly') &&
      date &&
      singleDate !== date &&
      pathname.includes('/date/')
    ) {
      replaceUrlParam({ paramNames: ['date'], newValue: singleDate })
    }
  }, [singleDate, type, date, pathname, replaceUrlParam])

  React.useEffect(() => {
    if (month && pathname.includes('/month/')) {
      replaceUrlParam({ paramNames: ['month'], newValue: month })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month])

  // Rok ze store'a z poprzedniej wizyty nie może nadpisać roku z URL — inaczej pętla 2025↔2026.
  const prevYearRef = React.useRef(year)
  React.useEffect(() => {
    const yearChanged = prevYearRef.current !== year
    prevYearRef.current = year
    if (!year || !yearChanged || year === yearParam) return
    replaceUrlParam({ paramNames: ['year'], newValue: year })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year])

  const sortedIds = [...vehiclesIds].sort((a: string, b: string) =>
    Number(a) > Number(b) ? 1 : -1,
  )

  return (
    <VehicleViewControlBarLayout
      blockedView={true}
      registrationNumber={vehicle?.registrationNumber || ''}
      onPrev={() => goPrevVehicleInActivity({ sortedIds, numericId: vehicleId })}
      onNext={() => goNextVehicleInActivity({ sortedIds, numericId: vehicleId })}
    >
      {(type === 'daily' || type === 'weekly') && (
        <DatePickerSingleButton
          iconAlign="left"
          size="small"
          label="Ustaw"
          clearable={false}
        />
      )}
      {type === 'monthly' && <MonthPickerButton />}
      {type === 'yearly' && (
        <YearPickerButton value={year} handleChangeValue={setYear} />
      )}
    </VehicleViewControlBarLayout>
  )
}

export default VehicleActivityBarWithDate
